import { describePhoneRejection, normalisePhone, type PhoneRejection } from './phone.util';

/**
 * IDENTIFIER RESOLUTION — ONE PATH (FR-5.8.4).
 *
 * "Login accepts either identifier on the dashboard, resolved through one
 * identifier-agnostic lookup — normalise, detect type, resolve to one officer. One code
 * path, never two."
 *
 * The difference between the dashboard and the officer surface is DATA, not a branch:
 * the caller passes `allow: ['phone', 'email']` or `allow: ['phone']` (OD-23). There is
 * no toggle in the UI and no second resolver on the server.
 */

export type Identity =
  /** Canonical 10 digits. */
  | { type: 'phone'; value: string }
  /** Trimmed and lowercased. Nothing else — see below. */
  | { type: 'email'; value: string };

export type IdentifierRejection = 'EMPTY' | 'NOT_ALLOWED' | 'BAD_EMAIL' | PhoneRejection;

export type IdentifierResult =
  { ok: true; identity: Identity } | { ok: false; reason: IdentifierRejection; message: string };

/**
 * Deliberately not `z.email()` — this file must not import zod, because it is called
 * from a keystroke handler in the browser. This is the shape check; the schema layer
 * re-validates with zod on the way in.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const MAX_IDENTIFIER_LENGTH = 254;

export const normaliseIdentifier = (
  raw: string,
  allow: readonly ('phone' | 'email')[] = ['phone', 'email'],
): IdentifierResult => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';

  if (trimmed.length === 0) {
    return { ok: false, reason: 'EMPTY', message: 'Enter your phone number or email address.' };
  }
  if (trimmed.length > MAX_IDENTIFIER_LENGTH) {
    return { ok: false, reason: 'BAD_EMAIL', message: 'That is too long to be a phone number or an email address.' };
  }

  /**
   * THE DETECTION RULE, ENTIRE: an `@` anywhere makes it an email.
   *
   * One character, one decidable answer. The alternative — try phone, fall back to
   * email — is order-dependent, produces different errors depending on which branch
   * failed last, and cannot be reasoned about from the outside. `@` is mandatory in
   * every email address and illegal in every phone format, so this is total.
   */
  if (trimmed.includes('@')) {
    if (!allow.includes('email')) {
      // OD-23 on the officer surface. A FORMAT rule, so it leaks nothing about accounts.
      return { ok: false, reason: 'NOT_ALLOWED', message: 'Enter your 10-digit mobile number.' };
    }
    const email = trimmed.toLowerCase();
    return EMAIL_SHAPE.test(email)
      ? { ok: true, identity: { type: 'email', value: email } }
      : { ok: false, reason: 'BAD_EMAIL', message: 'That does not look like a valid email address.' };
  }

  if (!allow.includes('phone')) {
    return { ok: false, reason: 'NOT_ALLOWED', message: 'Enter your email address.' };
  }

  const phone = normalisePhone(trimmed);
  return phone.ok
    ? { ok: true, identity: { type: 'phone', value: phone.phone } }
    : { ok: false, reason: phone.reason, message: describePhoneRejection(phone) };
};

/**
 * `.trim().toLowerCase()` and nothing else.
 *
 * Do NOT strip Gmail dots or `+tags`. Two officers' genuinely distinct addresses would
 * collide on the partial-unique index (FR-5.8.2), and the symptom is "cannot create
 * officer" on a perfectly valid address — a support ticket nobody can reproduce.
 */
export const normaliseEmail = (raw: string): string => raw.trim().toLowerCase();

/**
 * The rate-limit and lookup key. One string per identity, so `+91 98765 43210`,
 * `09876543210` and `9876543210` are the SAME bucket — which is the whole point of
 * normalising before limiting rather than limiting on the raw request body.
 */
export const identityKey = (identity: Identity): string => `${identity.type}:${identity.value}`;
